import React, { useEffect, useRef, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap"
import Paginations from "components/Common/Paginations"
import { useLocation, useNavigate } from "react-router-dom"
import Filters from "components/Common/Filters"
import { useDispatch } from "react-redux"
import {
  deleteCustomer,
  getAllCustomers,
  restoreCustomer,
  updateCustomerStatus,
} from "store/actions/customersAction"
import { useSelector } from "react-redux"
import { showAlert } from "store/reducers/alertReducer"
import { getPerPageOptions } from "components/Common/constants"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import isPermissionEnabled from "common/isPermissionEnabled"
import TableShimmer from "components/Shared/TableShimmer"
import formatDateTime from "components/Shared/formatDateTime"
import LtrText from "components/Shared/LtrText"
import { useTranslation } from "react-i18next"
import { setItemsPerPage } from "store/reducers/customersReducer"
import DeleteModal from "components/Common/DeleteModal"

const Customers = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t(" Deleted Customers List ")

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()

  const searchParams = new URLSearchParams(location.search)
  const page = parseInt(searchParams.get("page")) || 1
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
 const [showDeleteModal, setShowDeleteModal] = useState(false)
   const [itemIdToRestore, setItemIdToRestore] = useState(null)
  const [selectedFilters, setSelectedFilters] = useState({
    status: parseInt(searchParams.get("status") || "1"),
    sortOrder: parseInt(searchParams.get("sortOrder") || "1"),
  })

  const {
    loading,
    isLoading,
    customersList,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
  } = useSelector(state => state.Customers)

  const flatpickrRef = useRef(null)

  useEffect(() => {
    getAllCustomersList()
  }, [])

  const { userRole, accountType } = useSelector(state => state.Login)

  const [customersPermissions, setCustomersPermissions] = useState({
    list: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      7,
      "customersPermission_5",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setCustomersPermissions({
      list: isPermissionList,
    })
  }, [userRole])

  const filters = [
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
  ]

  const getAllCustomersList = () => {
    const { sortOrder } = selectedFilters
    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: 2,
      orderBy: sortOrder,
      keyword: searchTerm,
    }
    dispatch(getAllCustomers(payload))
  }

  const handleFetchCustomers = (page = 1) => {
    const { sortOrder } = selectedFilters
    const searchParams = new URLSearchParams()
    if (searchTerm) searchParams.set("search", searchTerm)
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())

    const payload = {
      perPage: 10,
      pageNo: page,
      status: 2,
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
    }

    dispatch(getAllCustomers(payload))
      .then(action => {
        const { statusCode, status } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/customers/deleted-customers?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        // Handle error if needed
      })
  }

  const handleSearch = () => {
    handleFetchCustomers(1)
  }

  const onPageChange = page => {
    handleFetchCustomers(page)
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => {
      const updatedFilters = {
        ...prevFilters,
        [filterName]: selectedValue,
      }

      if (filterName === "platform" && selectedValue.length === 0) {
        updatedFilters.appVersion = "" // Reset appVersion if platform is cleared
      }

      return updatedFilters
    })
  }

  const handleReset = () => {
    setSelectedFilters({
      sortOrder: 1,
    })
    setSearchTerm("")
    const payload = {
      perPage: 10,
      pageNo: 1,
      status: 2,
      orderBy: 1,
      keyword: "",
    }
    dispatch(getAllCustomers(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/customers/deleted-customers?page=1`)
        }
      })
      .catch(error => {})

    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.clear()
    }
  }

  const handleRestore = id => {
    const payload = {
      id: itemIdToRestore,
      status: "1",
    }
    dispatch(restoreCustomer(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowDeleteModal(false)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const perPageOptions = getPerPageOptions(totalCount)

  const handlePerPageChange = e => {
    dispatch(setItemsPerPage(Number(e.target.value)))
  }

  return (
    <div className="page-content  customers_page">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Deleted Customers List")}
                perPageOptions={perPageOptions}
                isCustomPage={true}
                itemsPerPage={itemsPerPage}
                handlePerPageChange={handlePerPageChange}
              />
              <CardBody>
                {/* {!loading && ( */}
                <Filters
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  handleReset={handleReset}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  isCustomPageSize={false}
                  searchBy={t("Name, Phone Number")}
                  colSize={3}
                  showOnlySearchAndSort={true}
                />
                {/* )} */}
                <div className="">
                  {loading ? (
                    <TableShimmer row={7} col={9} />
                  ) : customersList.length === 0 ? (
                    <p className="text-center my-3">
                      {t("No customers found")}
                    </p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light full_th">
                        <tr>
                          <th>{t("S. No.")}</th>
                          <th>{t("Name")}</th>
                          <th>{t("Phone Number")}</th>
                          <th>{t("Join Date")}</th>
                          <th>{t("Action")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {customersList.map(
                          ({ _id, name, phone, createdAt }, index) => (
                            <tr
                              key={index}
                              onClick={() =>
                                navigate(
                                  `/customers/deleted-customer-details?id=${_id}`
                                )
                              }
                              className="cursor-pointer"
                            >
                              <td scope="row">
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td>{name || "-"}</td>
                              <td>
                                {phone ? <LtrText>{phone}</LtrText> : "-"}
                              </td>

                              <td>{formatDateTime(createdAt) || "-"}</td>

                              <td>
                                <div className="d-flex gap-2">
                                  <div className="d-flex">
                                    <UncontrolledDropdown>
                                      <DropdownToggle
                                        tag="a"
                                        className="card-drop"
                                        onClick={e => e.stopPropagation()}
                                      >
                                        <i className="mdi mdi-dots-vertical font-size-18"></i>
                                      </DropdownToggle>
                                      <DropdownMenu className="dropdown-menu-end">
                                        <DropdownItem
                                          onClick={e => {
                                            e.stopPropagation()
                                            navigate(
                                              `/customers/deleted-customer-details?id=${_id}`
                                            )
                                          }}
                                        >
                                          <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
                                          {t("View")}
                                        </DropdownItem>

                                        <DropdownItem
                                          onClick={e => {
                                            e.stopPropagation()
                                            setShowDeleteModal(true)
                                            setItemIdToRestore(_id)
                                         
                                          }}
                                        >
                                          <i className="mdi mdi-restore font-size-16 text-warning me-1"></i>
                                          {t("Restore")}
                                        </DropdownItem>
                                      </DropdownMenu>
                                    </UncontrolledDropdown>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && customersList.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={customersList}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      <DeleteModal
        show={showDeleteModal}
        isLoading={isLoading}
        onDeleteClick={handleRestore}
        onCloseClick={() => setShowDeleteModal(false)}
        buttonText={t("Restore")}
        bodyTitle={t(
          "Are you sure you want to restore this account?"
        )}
        icon="mdi mdi-reload"
        btnClass="btn btn-primary"
      />
    </div>
  )
}

export default Customers
